import { css, StyleSheet } from 'aphrodite';
import { type FC, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { ITelephonyConfiguration } from '../../../extViewmodels';
import { Direction, type ITextMessage, TextStatus } from '../../../extViewmodels/TextMessagingViewModel';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { TextArea } from '../../../web/components-v2/TextArea';
import { THEME } from '../../../web/components-v2/Theme/theme';
import {
	apiErrorMessage,
	type FocusedLeadContact,
	type FocusedLeadContactPhone,
	useCreateTextConversation,
	useMarkConversationRead,
	useSendText,
	useTextConversation,
	useTextMessages,
} from '../data';

export type TextConversationDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	contact: FocusedLeadContact | undefined;
	phone: FocusedLeadContactPhone | undefined;
	/** The rep's texting number the message is sent from. */
	textingNumber: ITelephonyConfiguration | undefined;
};

const formatTime = (iso: string | undefined) =>
	iso
		? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
		: '';

const statusLabel = (message: ITextMessage) => {
	if (message.direction === Direction.Inbound) return 'Received';
	switch (message.status) {
		case TextStatus.Failed:
			return message.errorMessage ? `Failed: ${message.errorMessage}` : 'Failed';
		case TextStatus.Sending:
			return 'Sending…';
		default:
			return message.status;
	}
};

export const TextConversationDialog: FC<TextConversationDialogProps> = ({
	open,
	onOpenChange,
	contact,
	phone,
	textingNumber,
}) => {
	const [draft, setDraft] = useState('');
	const listRef = useRef<HTMLDivElement>(null);

	const conversationQuery = useTextConversation({
		phoneNumberId: textingNumber?.id,
		toE164: phone?.e164,
		enabled: open,
	});
	const conversation = conversationQuery.data ?? undefined;
	const messagesQuery = useTextMessages(conversation?.id, { enabled: open });
	const messages = messagesQuery.data ?? [];
	const sendText = useSendText();
	const createConversation = useCreateTextConversation();
	const markRead = useMarkConversationRead();

	const prevOpenRef = useRef(false);
	useEffect(() => {
		if (open && !prevOpenRef.current) setDraft('');
		prevOpenRef.current = open;
	}, [open]);

	const createdFor = useRef<string | null>(null);
	useEffect(() => {
		if (!open) {
			createdFor.current = null;
			return;
		}
		if (conversationQuery.data !== null || !textingNumber?.id || !phone?.e164) return;
		const pairKey = `${textingNumber.id}|${phone.e164}`;
		if (createdFor.current === pairKey) return;
		createdFor.current = pairKey;
		createConversation.mutate({ phoneNumberId: textingNumber.id, toE164: phone.e164 });
	}, [open, conversationQuery.data, textingNumber?.id, phone?.e164, createConversation]);

	const latestInboundId = messages.filter(m => m.direction === Direction.Inbound).at(-1)?.id ?? '';
	const markedReadFor = useRef<string | null>(null);
	useEffect(() => {
		if (!open) {
			markedReadFor.current = null;
			return;
		}
		if (!conversation?.id) return;
		const readKey = `${conversation.id}|${latestInboundId}`;
		if (markedReadFor.current === readKey) return;
		markedReadFor.current = readKey;
		markRead.mutate({ conversationId: conversation.id });
	}, [open, conversation?.id, latestInboundId, markRead]);

	useEffect(() => {
		const el = listRef.current;
		if (el) el.scrollTop = el.scrollHeight;
	}, [messages.length]);

	const canSend = !!conversation?.id && draft.trim().length > 0 && !sendText.isPending;

	const handleSend = async () => {
		if (!canSend || !conversation?.id) return;
		try {
			await sendText.mutateAsync({ conversationId: conversation.id, text: draft.trim() });
			setDraft('');
		} catch (err) {
			toast.error(apiErrorMessage(err, 'Failed to send text. Please try again.'));
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			void handleSend();
		}
	};

	const optedOut = !!conversation?.automatedSMSOptOutDate;
	const loadError = conversationQuery.error ?? createConversation.error ?? messagesQuery.error;
	const resolvingConversation =
		conversationQuery.isPending || (conversationQuery.data === null && !createConversation.error);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styles.popup]}>
				<DialogHeader>
					<div className={css(styles.headerCol)}>
						<DialogTitle>{contact ? `Text ${contact.name}` : 'Text'}</DialogTitle>
						<div className={css(styles.numbers)}>
							{phone && <span>To {phone.display}</span>}
							{textingNumber?.phoneNumber?.standard && <span>From {textingNumber.phoneNumber.standard}</span>}
						</div>
					</div>
				</DialogHeader>
				<DialogBody>
					{optedOut && (
						<div className={css(styles.optOutBanner)} role='status'>
							This contact has asked to stop receiving automated texts. Send only if they are expecting to hear from
							you.
						</div>
					)}
					<div ref={listRef} className={css(styles.messageList)} aria-live='polite'>
						{resolvingConversation || (messagesQuery.isPending && !!conversation) ? (
							<div className={css(styles.empty)}>Loading conversation…</div>
						) : loadError ? (
							<div className={css(styles.empty)}>{apiErrorMessage(loadError, 'Could not load this conversation.')}</div>
						) : messages.length === 0 ? (
							<div className={css(styles.empty)}>No messages yet. Say hello.</div>
						) : (
							messages.map(message => {
								const inbound = message.direction === Direction.Inbound;
								return (
									<div
										key={message.id}
										className={css(styles.bubbleRow, inbound ? styles.bubbleRowInbound : styles.bubbleRowOutbound)}
									>
										<div className={css(styles.bubble, inbound ? styles.bubbleInbound : styles.bubbleOutbound)}>
											{message.text}
										</div>
										<div className={css(styles.bubbleMeta)}>
											{formatTime(message.sentDate ?? message.creationDate)} · {statusLabel(message)}
										</div>
									</div>
								);
							})
						)}
					</div>
					<TextArea
						aria-label='Message'
						value={draft}
						onChange={e => setDraft(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder='Write a text…'
						rows={3}
						disabled={!conversation?.id || sendText.isPending}
						styles={[styles.input]}
					/>
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={() => onOpenChange(false)} disabled={sendText.isPending}>
						Close
					</Button>
					<Button variant='primary' onClick={handleSend} disabled={!canSend}>
						{sendText.isPending ? 'Sending…' : 'Send'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const styles = StyleSheet.create({
	popup: {
		maxWidth: 520,
		width: '92vw',
	},
	headerCol: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		paddingRight: THEME.spacing.lg,
	},
	numbers: {
		display: 'flex',
		gap: THEME.spacing.md,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	optOutBanner: {
		padding: THEME.spacing.sm,
		marginBottom: THEME.spacing.sm,
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.danger.light,
		color: THEME.colors.danger.dark,
		fontSize: THEME.typography.fontSizes.xs,
	},
	messageList: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		height: 320,
		overflowY: 'auto',
		padding: THEME.spacing.sm,
		marginBottom: THEME.spacing.sm,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.background.light,
	},
	empty: {
		margin: 'auto',
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	bubbleRow: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		maxWidth: '80%',
	},
	bubbleRowInbound: {
		alignSelf: 'flex-start',
		alignItems: 'flex-start',
	},
	bubbleRowOutbound: {
		alignSelf: 'flex-end',
		alignItems: 'flex-end',
	},
	bubble: {
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
		borderRadius: THEME.common.borderRadius,
		fontSize: THEME.typography.fontSizes.sm,
		whiteSpace: 'pre-wrap',
		overflowWrap: 'anywhere',
	},
	bubbleInbound: {
		background: THEME.colors.background.main,
		border: `1px solid ${THEME.colors.border.main}`,
		color: THEME.colors.text.main,
	},
	bubbleOutbound: {
		background: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
	},
	bubbleMeta: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	input: {
		width: '100%',
		resize: 'vertical',
	},
});
